import restaurantData from "../data/demo/restaurant.json";
import dishesData from "../data/demo/dishes.json";
import menusData from "../data/demo/menus.json";
import todaysSpecialsData from "../data/demo/todaysspecials.json";
import ordersData from "../data/demo/orders.json";
import partnersData from "../data/demo/partners.json";
import ratingsData from "../data/demo/ratings.json";
import customersData from "../data/demo/customers.json";
import categoriesData from "../data/demo/categories.json";
import employeesData from "../data/demo/employees.json";

// Demo restaurant ID
const DEMO_RESTAURANT_ID = 999;
const DEMO_USER_ID = 999;

// Helper to check if we're on /demo route
const isDemoRoute = () => {
  if (typeof window === "undefined") return false;
  return window.location.pathname.includes("/demo");
};

// Helper to check if URL is a public data endpoint that should reach the backend
const isPublicDataEndpoint = (url) => {
  const publicDataEndpoints = [
    "/dishtype/",
    "/allergy/",
    "/city/",
    "/restauranttype/",
    "/consumptionmode/",
    "/region/",
    "/commune/",
    "/country/",
    "/foodtag/",
    "/offertype/",
    "/pricecategory/",
    "/language/",
    "/refundmotive/",
    "/role/",
  ];
  return publicDataEndpoints.some((endpoint) => url.includes(endpoint));
};

// Helper to simulate API delay
const delay = (ms = 300) => new Promise((resolve) => setTimeout(resolve, ms));

// Helper to extract ID from URL
const extractIdFromUrl = (url, pattern) => {
  const match = url.match(pattern);
  return match ? parseInt(match[1]) : null;
};

// Helper to add fullImagePath to dishes
const addFullImagePath = (dish) => {
  if (!dish) return dish;
  return {
    ...dish,
    fullImagePath: dish.imagePath || null,
  };
};

// Helper to add fullImagePath to array of dishes
const addFullImagePathToDishes = (dishes) => {
  if (!Array.isArray(dishes)) return dishes;
  return dishes.map(addFullImagePath);
};

// Mock response creator
const createMockResponse = (data, status = 200) => ({
  data,
  status,
  statusText: "OK",
  headers: {},
  config: {},
});

// API endpoint mapping
const apiMappings = {
  // Restaurant endpoints
  "/restaurant/settings/": (url) => {
    const id = extractIdFromUrl(url, /\/restaurant\/settings\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      return createMockResponse(restaurantData);
    }
    throw new Error("Restaurant not found");
  },
  "/restaurant/ratings/": (url) => {
    const id = extractIdFromUrl(url, /\/restaurant\/ratings\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      return createMockResponse(ratingsData);
    }
    throw new Error("Restaurant not found");
  },
  "/restaurant/customersStats/": () => {
    return createMockResponse(customersData);
  },
  "/restaurant/partners/": (url) => {
    const id = extractIdFromUrl(url, /\/restaurant\/partners\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      return createMockResponse(partnersData);
    }
    return createMockResponse([]);
  },
  "/restaurant/allByManager/": (url) => {
    const id = extractIdFromUrl(url, /\/restaurant\/allByManager\/(\d+)/);
    if (id === DEMO_USER_ID) {
      return createMockResponse([restaurantData]);
    }
    return createMockResponse([]);
  },
  "/restaurant/createdAt/": (url) => {
    const id = extractIdFromUrl(url, /\/restaurant\/createdAt\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      return createMockResponse({ createdAt: restaurantData.createdAt });
    }
    throw new Error("Restaurant not found");
  },
  "/restaurant/orders/": (url) => {
    const id = extractIdFromUrl(url, /\/restaurant\/orders\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      return createMockResponse(ordersData);
    }
    return createMockResponse([]);
  },

  // Dish endpoints
  "/dish/byRestaurant/": (url) => {
    const id = extractIdFromUrl(url, /\/dish\/byRestaurant\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      return createMockResponse(addFullImagePathToDishes(dishesData));
    }
    return createMockResponse([]);
  },
  "/dish/byType/": (url, params) => {
    const id = extractIdFromUrl(url, /\/dish\/byType\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID && params?.typeId) {
      const filtered = dishesData.filter((d) => d.typeId === params.typeId);
      return createMockResponse(addFullImagePathToDishes(filtered));
    }
    return createMockResponse([]);
  },
  "/dish/": (url) => {
    const id = extractIdFromUrl(url, /\/dish\/(\d+)$/);
    if (id) {
      const dish = dishesData.find((d) => d.dishId === id);
      if (dish) {
        return createMockResponse(addFullImagePath(dish));
      }
    }
    throw new Error("Dish not found");
  },
  "/dishcategory/restaurant/": (url) => {
    const id = extractIdFromUrl(url, /\/dishcategory\/restaurant\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      return createMockResponse(categoriesData);
    }
    return createMockResponse([]);
  },

  // Menu endpoints
  "/menu/byRestaurant/": (url) => {
    const id = extractIdFromUrl(url, /\/menu\/byRestaurant\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      // Enrich menus with full dish data including fullImagePath
      const enrichedMenus = menusData.map((menu) => ({
        ...menu,
        dishes: menu.dishes.map((dishRef) => {
          const fullDish = dishesData.find((d) => d.dishId === dishRef.dishId);
          return fullDish ? addFullImagePath(fullDish) : dishRef;
        }),
      }));
      return createMockResponse(enrichedMenus);
    }
    return createMockResponse([]);
  },
  "/menu/": (url) => {
    const id = extractIdFromUrl(url, /\/menu\/(\d+)$/);
    if (id) {
      const menu = menusData.find((m) => m.menuId === id);
      if (menu) {
        // Enrich menu dishes with full dish data including fullImagePath
        const enrichedMenu = {
          ...menu,
          dishes: menu.dishes.map((dishRef) => {
            const fullDish = dishesData.find((d) => d.dishId === dishRef.dishId);
            return fullDish ? addFullImagePath(fullDish) : dishRef;
          }),
        };
        return createMockResponse(enrichedMenu);
      }
    }
    throw new Error("Menu not found");
  },

  // Today's Special endpoints
  "/todaysspecial/byRestaurant/": (url) => {
    const id = extractIdFromUrl(url, /\/todaysspecial\/byRestaurant\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      return createMockResponse(todaysSpecialsData);
    }
    return createMockResponse([]);
  },
  "/todaysspecial/": (url) => {
    const id = extractIdFromUrl(url, /\/todaysspecial\/(\d+)$/);
    if (id) {
      const special = todaysSpecialsData.find((s) => s.todaysSpecialId === id);
      if (special) {
        return createMockResponse(special);
      }
    }
    throw new Error("Today's special not found");
  },

  // Order endpoints
  "/order/restaurant/": (url, params) => {
    // Handle both /order/restaurant/:id and /order/restaurant/:id/success
    const body = params || {};
    let filteredOrders = [...ordersData];
    
    // Filter by date range if provided
    if (body.from && body.to) {
      filteredOrders = ordersData.filter((order) => {
        const orderDate = new Date(order.startAt);
        return orderDate >= new Date(body.from) && orderDate <= new Date(body.to);
      });
    }
    
    // If it's the success endpoint, only return successful orders
    if (url.includes("/success")) {
      filteredOrders = filteredOrders.filter((order) => order.statusId === 5);
    }
    
    return createMockResponse(filteredOrders);
  },

  // Employee endpoints
  "/restaurant/employees/": (url) => {
    const id = extractIdFromUrl(url, /\/restaurant\/employees\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      return createMockResponse({ employees: employeesData });
    }
    return createMockResponse({ employees: [] });
  },
  "/restaurant/addEmployee/": () => {
    // Return success response for creating employee
    return createMockResponse({ success: true });
  },
  "/dish/relatedItems/": (url) => {
    const id = extractIdFromUrl(url, /\/dish\/relatedItems\/(\d+)/);
    if (id) {
      // Return empty arrays for related items in demo
      return createMockResponse({ menus: [], todaysspecials: [] });
    }
    return createMockResponse({ menus: [], todaysspecials: [] });
  },
  "/dish/active/": (url) => {
    // Toggle dish active status - return success
    const id = extractIdFromUrl(url, /\/dish\/active\/(\d+)/);
    if (id) {
      const dish = dishesData.find((d) => d.dishId === id);
      if (dish) {
        // Toggle the active status in the data
        dish.isActive = !dish.isActive;
        return createMockResponse(addFullImagePath(dish));
      }
    }
    return createMockResponse({ success: true });
  },
  "/dish/order/": () => {
    // Return updated dishes after reordering
    return createMockResponse(addFullImagePathToDishes(dishesData));
  },
  "/dishcategory/order/": () => {
    // Return updated categories after reordering
    return createMockResponse(categoriesData);
  },
  "/menu/order/": () => {
    // Return updated menus after reordering
    return createMockResponse(menusData);
  },
  "/menu/active/": () => {
    // Toggle menu active status
    return createMockResponse({ success: true });
  },
  "/todaysspecial/order/": () => {
    // Return updated todaysspecials after reordering
    return createMockResponse(todaysSpecialsData);
  },
  "/todaysspecial/active/": () => {
    // Toggle todaysspecial active status
    return createMockResponse({ success: true });
  },
  "/user/": (url) => {
    // Handle both /user/:id and /user/details/:id
    let id;
    if (url.includes("/details/")) {
      id = extractIdFromUrl(url, /\/user\/details\/(\d+)/);
    } else {
      id = extractIdFromUrl(url, /\/user\/(\d+)$/);
    }
    if (id === DEMO_USER_ID || id === 1000 || id === 1001) {
      const user = employeesData.find((e) => e.userId === id);
      if (user) {
        return createMockResponse(user);
      }
    }
    throw new Error("User not found");
  },
  // Offer endpoints
  "/offertype/all": () => {
    // Mock offer types
    return createMockResponse([
      { offerTypeId: 1, label: "percentage", requiresValue: true },
      { offerTypeId: 2, label: "fixed", requiresValue: true },
      { offerTypeId: 3, label: "freeDelivery", requiresValue: false },
    ]);
  },
  "/restaurant/offer/": (url) => {
    const id = extractIdFromUrl(url, /\/restaurant\/offer\/(\d+)$/);
    if (id) {
      // Mock offer data
      return createMockResponse({
        offerId: id,
        restaurantId: DEMO_RESTAURANT_ID,
        distance: 5,
        maximumSubscribers: 100,
        offertype: { offerTypeId: 1, label: "percentage", requiresValue: true },
        value: 10,
      });
    }
    throw new Error("Offer not found");
  },
  "/restaurant/restauranttype/": (url) => {
    const id = extractIdFromUrl(url, /\/restaurant\/restauranttype\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID) {
      return createMockResponse(restaurantData.restauranttype);
    }
    throw new Error("Restaurant not found");
  },
  "/restaurant/image/": (url) => {
    const id = extractIdFromUrl(url, /\/restaurant\/image\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID && restaurantData.images && restaurantData.images.length > 0) {
      return createMockResponse({ imagePath: restaurantData.images[0].fullImagePath });
    }
    return createMockResponse({ imagePath: null });
  },
  "/restaurant/partners/offer/": (url, params) => {
    const id = extractIdFromUrl(url, /\/restaurant\/partners\/offer\/(\d+)/);
    if (id === DEMO_RESTAURANT_ID && params?.offerId) {
      // Return related partners for the offer
      return createMockResponse(partnersData.slice(0, 3));
    }
    return createMockResponse([]);
  },
  "/restaurant/stripeStatus/": (url) => {
    // Handle URL with language parameter: /restaurant/stripeStatus/999/fr
    // Also handle URLs with /api prefix or full domain
    const match = url.match(/\/restaurant\/stripeStatus\/(\d+)(?:\/|$|\/[^/]+)/);
    const id = match ? parseInt(match[1]) : null;
    if (id === DEMO_RESTAURANT_ID) {
      // Return mock stripe status
      return createMockResponse({
        stripeDone: true,
        stripeUrl: "https://stripe.com/demo-setup",
      });
    }
    return createMockResponse({ stripeDone: false, stripeUrl: null });
  },
};

// Custom adapter that returns mock data without making HTTP calls
const createDemoAdapter = (config) => {
  return new Promise(async (resolve, reject) => {
    const url = config.url || "";
    const method = (config.method || "get").toLowerCase();
    const params = config.params || config.data || {};

    // Normalize URL - remove base URL and /api prefix if present
    const normalizedUrl = url.replace(/^https?:\/\/[^/]+/, "").replace(/^\/api/, "");

    // Find matching endpoint
    for (const [endpoint, handler] of Object.entries(apiMappings)) {
      if (normalizedUrl.includes(endpoint) || url.includes(endpoint)) {
        try {
          await delay();
          const mockResponse = handler(url, params);
          resolve(mockResponse);
          return;
        } catch (err) {
          reject({
            response: {
              status: 404,
              data: { message: err.message },
            },
            message: err.message,
          });
          return;
        }
      }
    }

    // Handle unmapped requests
    await delay();
    if (method === "post" || method === "put" || method === "delete") {
      resolve(createMockResponse({ success: true }, 200));
    } else {
      resolve(createMockResponse([], 200));
    }
  });
};

// Intercept axios requests
let requestInterceptorId = null;

export const interceptDemoApi = (axiosInstance) => {
  // Remove existing interceptor if any
  if (requestInterceptorId !== null) {
    axiosInstance.interceptors.request.eject(requestInterceptorId);
  }

  // Add request interceptor to use custom adapter in demo mode
  requestInterceptorId = axiosInstance.interceptors.request.use(
    async (config) => {
      if (isDemoRoute()) {
        const url = config.url || "";
        // Only override adapter for non-public endpoints
        // Public data endpoints should reach the backend
        if (!isPublicDataEndpoint(url)) {
          // Override adapter to prevent HTTP calls
          config.adapter = createDemoAdapter;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return requestInterceptorId;
};

// Check if demo mode is active (route-based)
export const isDemoMode = () => {
  return isDemoRoute();
};
